/**
 * Décorations achetables en gemmes (ROADMAP 2.4, G§ lot 2).
 *
 * ══ POURQUOI C'EST LE MEILLEUR DÉBOUCHÉ POSSIBLE POUR LES GEMMES ═════════════
 *
 * Le §14 interdit le pay-to-win sur la production. Une décoration ne produit **rien** —
 * pas « peu », rien du tout — donc la frontière n'est pas à surveiller : elle est vraie
 * par construction, et aucun équilibrage futur ne peut la faire glisser.
 *
 * C'est aussi la seule dépense qui laisse une TRACE à l'écran. L'avance de production
 * disparaît dans le solde, la nuit blanche se consomme, l'extension de terrain se remplit
 * de bâtiments. Une fontaine reste. Le joueur voit ce qu'il a payé, chaque fois qu'il
 * ouvre le jeu — c'est ce qui sépare un achat dont on se souvient d'un achat qu'on
 * regrette.
 *
 * ══ Aucun asset nouveau ══════════════════════════════════════════════════════
 *
 * Les douze sprites listés ici sont **déjà** dans `public/assets/buildings/`, rendus par
 * le pipeline et déjà utilisés comme décor d'ambiance ou comme traces de visiteurs. Le
 * pack POLYGON MINI en fournit largement de quoi remplir trois pages sans un fichier de
 * plus — ce lot ne coûte donc pas une passe de rendu.
 *
 * ══ Les prix, et pourquoi l'échelle a été REFAITE (ROADMAP 2.9b) ═════════════
 *
 * La 0.62.0 posait une échelle volontairement PLATE — de 4 à 40 — et je l'avais
 * défendue ainsi : « une décoration n'a aucune utilité mécanique, la faire coûter cher
 * n'ajouterait pas de la valeur, ça ajouterait de la frustration ».
 *
 * **Benoit a demandé l'inverse, et son argument est meilleur que le mien.** Une pièce
 * d'exception à 300 gemmes n'est pas un péage : c'est un OBJECTIF. Et c'est exactement
 * ce que le §1 réclame — avoir en permanence quelque chose à viser — pendant que le
 * défaut n° 1 du G§1 réclame la même chose côté boutique : donner une cible au plus
 * gros pack. Une échelle plate ne peut faire ni l'un ni l'autre. Mon argument valait
 * pour la FRUSTRATION d'un péage obligatoire ; il ne vaut pas pour un objet qu'on
 * convoite et dont on se passe.
 *
 * L'échelle couvre donc quatre des cinq paliers du G§2 :
 *
 * | Palier        | Prix      | Ce qu'on y trouve ici            |
 * | ------------- | --------- | -------------------------------- |
 * | **Impulsion** | 3 – 8     | bosquet, rochers, poubelle       |
 * | **Quotidien** | 10 – 25   | les quatre arbres, plantes       |
 * | **Journée**   | 40 – 90   | jardinière, banc                 |
 * | **Identité**  | 120 – 300 | lampadaire, **fontaine**         |
 *
 * Ce qui NE bouge pas, et qui est un contrat écrit : l'entrée reste **sous 10 gemmes**,
 * accessible dans les vingt premières minutes — le critère de sortie déclaré du lot 2
 * de `GEMMES.md`. Rendre le catalogue désirable ne doit pas fermer sa porte d'entrée :
 * un joueur qui n'a jamais dépensé n'achètera jamais.
 *
 * ⚠️ Ces prix changent la capacité de dépense d'une ère, donc le chiffre du ROADMAP 2.7.
 * Il se MESURE dans `tests/gemEconomy.test.ts`, il ne se suppose pas.
 */

import type { IconName } from '@/ui/icons';

export interface DecorationDefinition {
  /** Identifiant stable, persisté dans la sauvegarde. Ne jamais le renommer. */
  readonly id: string;
  /**
   * Sprite, tel que le renderer le connaît. C'est aussi le `kind` de l'entité posée :
   * une décoration achetée EST une entité de décor, elle réutilise le même rendu.
   */
  readonly sprite: string;
  readonly gemCost: number;
  /** Clé i18n du nom affiché. Le nom d'un objet se traduit (règle 9). */
  readonly labelKey: string;
  readonly icon: IconName;
}

/**
 * Douze décorations, du bosquet à la fontaine.
 *
 * L'ordre est celui de la boutique, et il monte en prix : la première ligne est ce qu'un
 * joueur du premier soir peut s'offrir, la dernière ce qu'il vise — et ce qu'il visera
 * encore dans plusieurs semaines, ce qui est tout l'intérêt (2.9b).
 */
export const DECORATIONS: readonly DecorationDefinition[] = [
  // Impulsion (3 – 8) : la porte d'entrée, franchissable le premier soir.
  { id: 'bush', sprite: 'bush1', gemCost: 4, labelKey: 'decor.bush', icon: 'flower' },
  { id: 'rock', sprite: 'rock1', gemCost: 4, labelKey: 'decor.rock', icon: 'flower' },
  { id: 'rockTall', sprite: 'rock2', gemCost: 6, labelKey: 'decor.rockTall', icon: 'flower' },
  { id: 'bin', sprite: 'bin', gemCost: 8, labelKey: 'decor.bin', icon: 'flower' },
  // Quotidien (10 – 25) : ce qu'on s'offre en passant, une ou deux fois par jour.
  { id: 'tree', sprite: 'tree1', gemCost: 12, labelKey: 'decor.tree', icon: 'flower' },
  { id: 'treeRound', sprite: 'tree2', gemCost: 15, labelKey: 'decor.treeRound', icon: 'flower' },
  { id: 'treeTall', sprite: 'tree3', gemCost: 20, labelKey: 'decor.treeTall', icon: 'flower' },
  {
    id: 'potplants',
    sprite: 'potplants',
    gemCost: 25,
    labelKey: 'decor.potplants',
    icon: 'flower',
  },
  // Journée (40 – 90) : deux ou trois jours de quêtes, une petite décision.
  {
    id: 'flowerbed',
    sprite: 'flowerbed',
    gemCost: 45,
    labelKey: 'decor.flowerbed',
    icon: 'flower',
  },
  { id: 'bench', sprite: 'bench', gemCost: 70, labelKey: 'decor.bench', icon: 'flower' },
  /*
    Identité (120 – 300) : les deux pièces d'exception. Ce sont ELLES la demande de
    Benoit — quelque chose qui se vise sur plusieurs semaines de jeu gratuit, et vers
    quoi un gros pack a enfin une raison d'exister.
  */
  { id: 'lamp', sprite: 'streetLight', gemCost: 120, labelKey: 'decor.lamp', icon: 'flower' },
  { id: 'fountain', sprite: 'fountain', gemCost: 300, labelKey: 'decor.fountain', icon: 'flower' },
];

const BY_ID = new Map(DECORATIONS.map((entry) => [entry.id, entry]));

export function decorationById(id: string): DecorationDefinition | undefined {
  return BY_ID.get(id);
}

const BY_SPRITE = new Map(DECORATIONS.map((entry) => [entry.sprite, entry]));

/**
 * L'entrée de catalogue d'un sprite. Les sprites sont uniques par construction —
 * `decorations.test.ts` le verrouille — donc la réponse est sans ambiguïté. C'est par ce
 * chemin qu'une entité posée (qui ne connaît que son `kind`) retrouve son prix d'achat.
 */
export function decorationBySprite(sprite: string): DecorationDefinition | undefined {
  return BY_SPRITE.get(sprite);
}

/**
 * Sortes de traces SEMÉES par les visiteurs dont le nom ne désigne pas un sprite.
 * Les paliers `arch` et `goldenStatue` n'ont AUCUN modèle dans le pack : ils sont
 * volontairement absents — l'état les garde, l'écran les attendra.
 */
const VISITOR_KIND_SPRITES: Record<string, string> = {
  flowerbed: 'flowerbed',
  planter: 'potplants',
  bench: 'bench',
  fountain: 'fountain',
};

/**
 * Sprite qui rend une entité de décor, ou `null` si la sorte n'a pas de modèle.
 *
 * ⚠️ DÉRIVÉ du catalogue, complété par les seuls alias des visiteurs — jamais une table
 * énumérée à la main (règle 15). La table manuscrite du renderer ne connaissait que
 * quatre sortes : les douze décorations de la 0.62.0 posaient `bush1`, `tree1`,
 * `streetLight`… et NEUF d'entre elles étaient invisibles après achat — payées,
 * présentes dans l'état, jamais rendues, donc jamais tapables non plus (`traceAt` ne
 * parcourt que les sprites vivants). Une décoration ajoutée au catalogue est désormais
 * rendable par construction.
 */
export function traceSpriteFor(kind: string): string | null {
  const alias = VISITOR_KIND_SPRITES[kind];
  if (alias !== undefined) return alias;
  return BY_SPRITE.has(kind) ? kind : null;
}
